import { useState, useLayoutEffect, useMemo } from 'react';

// Blink incorrectly considers leading spaces in CSS property value declarations to be significant:
//   --foo: bar;
//   Firefox, Safari: window.getComputedStyle(el).getPropertyValue('--foo')  => 'bar'
//   Chrome, Edge:    window.getComputedStyle(el).getPropertyValue('--foo')  => ' bar'
// Thus we need to trim values so that different browsers will get the same results.
// Relevant CSS spec:
// https://www.w3.org/TR/css-variables-1/#serializing-custom-props
//   "Note that the leading whitespace on the value is automatically trimmed by the CSS parser; it’s not preserved here."
// https://www.w3.org/TR/CSS21/syndata.html#tokenization
//   declaration : property S* ':' S* value;
function trimValue(value) {
  const maybeTrimmed = typeof value === 'string' ? value.trimStart() : value;
  return maybeTrimmed;
}

/**
 * Retrieves the value of a theme CSS variable.
 */

function useThemeVariable(nameOrNames, ref) {
  const [values, setValues] = useState([]);
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || ref.current == null) {
      return;
    }
    const computedStyle = window.getComputedStyle(ref.current);
    let cssVariableValues;
    if (typeof nameOrNames === 'string') {
      const variableValue = computedStyle.getPropertyValue(`--${nameOrNames}`);
      cssVariableValues = [[nameOrNames, trimValue(variableValue)]];
    } else {
      cssVariableValues = nameOrNames.map(variableName => {
        const variableValue = computedStyle.getPropertyValue(`--${variableName}`);
        return [variableName, trimValue(variableValue)];
      });
    }
    setValues(cssVariableValues);
  }, [nameOrNames, ref]);
  return useMemo(() => {
    return typeof nameOrNames === 'string' ? values[0]?.[1] : values.reduce((variableRecord, _ref) => {
      let [variableName, variableValue] = _ref;
      variableRecord[variableName] = variableValue;
      return variableRecord;
    }, {});
  }, [nameOrNames, values]);
}

export { useThemeVariable };
